import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@structural-climbing/ui'
import { useEmailCode } from './useEmailCode'
import type { EmailVerified, GuestEmail } from './useEmailCode'

interface Props {
  /** The address the code goes to, shown so the person knows where to look. */
  email: string
  /** Set for someone without an account. Omit for a signed-in member. */
  guest?: GuestEmail
  onVerified: (result: EmailVerified) => void
}

/**
 * "We sent you a code": the one screen that proves an email address, used on
 * the profile and before signing. It sends the code as soon as it appears,
 * because every place that shows it has already decided a code is needed.
 */
export function EmailCodeForm({ email, guest, onVerified }: Props) {
  const { t } = useTranslation()
  const { send, verify, sending, sent, checking, error, cooldown, alreadyVerified } =
    useEmailCode(guest)
  const [code, setCode] = useState('')

  // Once, on mount. React runs effects twice in development, and two sends
  // would have the second one refused as too soon.
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    void send()
  }, [send])

  useEffect(() => {
    if (alreadyVerified) onVerified(alreadyVerified)
    // onVerified is the caller's and need not be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyVerified])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const result = await verify(code)
    if (result) onVerified(result)
    else setCode('')
  }

  const complete = /^\d{6}$/.test(code)

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
      <p className="text-sm text-foreground">
        {sent ? t('emailCode.sentTo', { email }) : t('emailCode.sendingTo', { email })}
      </p>
      <Input
        label={t('emailCode.codeLabel')}
        value={code}
        onValueChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        isDisabled={checking}
        description={t('emailCode.hint')}
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {t(`emailCode.error.${error}`)}
        </p>
      )}
      <Button type="submit" color="primary" isLoading={checking} isDisabled={!complete || checking}>
        {t('emailCode.confirm')}
      </Button>
      <Button
        variant="light"
        size="sm"
        isLoading={sending}
        isDisabled={sending || cooldown > 0}
        onClick={() => void send()}
      >
        {cooldown > 0 ? t('emailCode.resendIn', { seconds: cooldown }) : t('emailCode.resend')}
      </Button>
    </form>
  )
}
